<template>
   <!-- Create/Edit User Modal -->
    <CreateUpdateUserModal
      :show-create-form="showCreateForm"
      :editing-user="editingUser"
      :user-form="userForm"
      :saving="saving"
      @close="closeModal"
      @save="saveUser"
    />
    
    <!-- Import Users Modal -->
    <ImportUsersModal
      :show="showImportModal"
      @close="showImportModal = false"
      @imported="handleImportComplete"
    />
    
  <div class="user-management">
    <div class="header-section">
      <h2 class="page-title">👥 {{ $t('userManagement.title') }}</h2>
      <div class="header-actions">
        <button v-if="isSuperuser" @click="showImportModal = true" class="import-btn">
          📥 {{ $t('userManagement.importUsers') }}
        </button>
        <button @click="showCreateForm = true" class="create-user-btn">
          ➕ {{ $t('userManagement.addNewUser') }}
        </button>
      </div>
    </div>

    <!-- Search and Filter with Month Picker -->
    <div class="filters">
      <div class="filter-group-left">
        <div class="filter-group">
          <label for="search">{{ $t('userManagement.searchUsers') }}:</label>
          <input 
            v-model="searchQuery" 
            id="search"
            type="text" 
            :placeholder="$t('userManagement.searchPlaceholder')"
            class="search-input"
          />
        </div>
        
        <div class="filter-group">
          <label for="role-filter">{{ $t('userManagement.filterByRole') }}:</label>
          <select v-model="roleFilter" id="role-filter">
            <option value="">{{ $t('userManagement.allRoles') }}</option>
            <option value="worker">{{ $t('userManagement.workers') }}</option>
            <option value="manager">{{ $t('userManagement.managers') }}</option>
          </select>
        </div>
      </div>

      <!-- Month Picker -->
      <div class="filter-group month-picker-group">
        <label>📅 {{ $t('common.month') }}:</label>
        <div class="month-picker-compact">
          <button @click="previousMonth" class="month-nav-btn-small">◀</button>
          <span class="current-month-small">{{ formatMonthYear(selectedDate) }}</span>
          <button @click="nextMonth" class="month-nav-btn-small">▶</button>
        </div>
      </div>
    </div>

    <!-- Users Table -->
    <div class="users-section">
      <div v-if="loading" class="loading">
        <div class="loading-spinner"></div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <div v-else-if="filteredUsers.length === 0" class="no-users">
        <div class="empty-state">
          <div class="empty-icon">👥</div>
          <h3>{{ $t('userManagement.noUsersFound') }}</h3>
          <p>{{ users.length === 0 ? $t('userManagement.startByCreating') : $t('userManagement.tryAdjustingFilters') }}</p>
        </div>
      </div>

      <div v-else class="users-table">
        <table>
          <thead>
            <tr>
              <th>{{ $t('userProfile.firstName') }} {{ $t('userProfile.lastName') }}, {{ $t('userProfile.role') }}</th>
              <th>{{ $t('userProfile.employmentType') }}</th>
              <th>{{ $t('userProfile.monthlySalary') }}</th>
              <th>{{ $t('pdf.ob1') }} (1.3x)</th>
              <th>{{ $t('pdf.ob2') }} (1.5x)</th>
              <th>{{ $t('pdf.ob3') }} (2.0x)</th>
              <th>{{ $t('userManagement.grossSalary') }}</th>
              <th>{{ $t('userManagement.employeeTax') }}</th>
              <th>{{ $t('userManagement.employerTax') }}</th>
              <th>{{ $t('userProfile.netSalary') }}</th>
              <th>{{ $t('userManagement.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="user in filteredUsers" 
              :key="user.id"
            >
              <td>
                <span class="user-name">
                  {{ user.first_name }} {{ user.last_name }}
                  <span class="role-badge" :class="user.role">{{ $t('roles.' + user.role) }}</span>
                </span>
              </td>
              <td>
                <!-- Debug: print payroll object for this user -->
                <span v-if="user.payroll?.employment_type">{{ $t('userProfile.' + user.payroll.employment_type) }}</span>
                <span v-else>-</span>
              </td>
              <td>
                <span class="salary" v-if="hasPayrollData(user)">{{ formatCurrency(calculateBaseSalary(user)) }}</span>
                <span v-else class="no-salary">-</span>
              </td>
              <td class="ob-hours-cell">
                <span class="ob-hours-display">{{ formatOBHours(user.id, 'ob1_hours') }}</span>
              </td>
              <td class="ob-hours-cell">
                <span class="ob-hours-display">{{ formatOBHours(user.id, 'ob2_hours') }}</span>
              </td>
              <td class="ob-hours-cell">
                <span class="ob-hours-display">{{ formatOBHours(user.id, 'ob3_hours') }}</span>
              </td>
              <td>
                <span class="salary" v-if="hasPayrollData(user)">{{ formatCurrency(calculateGrossSalary(user)) }}</span>
                <span v-else class="no-data">-</span>
              </td>
              <td>
                <span class="tax-amount" v-if="hasPayrollData(user)">{{ formatCurrency(calculateEmployeeTax(user)) }}</span>
                <span v-else class="no-data">-</span>
              </td>
              <td>
                <span class="tax-amount" v-if="hasPayrollData(user)">{{ formatCurrency(calculateEmployerTax(user)) }}</span>
                <span v-else class="no-data">-</span>
              </td>
              <td>
                <span class="salary" v-if="hasPayrollData(user)">
                  {{ formatCurrency(calculateGrossSalary(user) - calculateEmployeeTax(user)) }}
                </span>
                <span v-else class="no-data">-</span>
              </td>
              <td class="actions">
                <button @click="editUser(user)" class="action-btn edit">
                  {{ $t('common.edit') }}
                </button>
                <button @click="deleteUser(user)" class="action-btn delete" :disabled="user.id === currentUserId">
                  {{ $t('common.delete') }}
                </button>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="totals-row">
              <td colspan="3" class="totals-label">
                <strong>{{ $t('userManagement.totalMonthlyCost') }}</strong>
              </td>
              <td class="totals-ob">
                <strong>{{ totalOB1Hours }}</strong>
              </td>
              <td class="totals-ob">
                <strong>{{ totalOB2Hours }}</strong>
              </td>
              <td class="totals-ob">
                <strong>{{ totalOB3Hours }}</strong>
              </td>
              <td class="totals-amount">
                <strong>{{ formatCurrency(totalGrossSalary) }}</strong>
              </td>
              <td class="totals-amount">
                <strong>{{ formatCurrency(totalEmployeeTax) }}</strong>
              </td>
              <td class="totals-amount">
                <strong>{{ formatCurrency(totalEmployerTax) }}</strong>
              </td>
              <td class="totals-amount">
                <strong>{{ formatCurrency(totalWorkerPayments) }}</strong>
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->

    <!-- Messages -->
    <div v-if="message" :class="['message', messageType]">
      {{ message }}
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="userToDelete" class="modal-overlay" @click="cancelDelete">
      <div class="modal-content delete-modal" @click.stop>
        <div class="delete-header">
          <h3>⚠️ {{ $t('userManagement.confirmDelete') }}</h3>
        </div>
        
        <div class="delete-body">
          <p>{{ $t('userManagement.confirmDeleteMessage') }}:</p>
          <div class="user-info">
            <strong>{{ userToDelete.first_name }} {{ userToDelete.last_name }}</strong>
            <span class="user-detail">{{ userToDelete.email }}</span>
          </div>
          <p class="warning-text">{{ $t('userManagement.cannotUndo') }}</p>
        </div>
        
        <div class="delete-actions">
          <button @click="cancelDelete" class="cancel-delete-btn">
            {{ $t('common.cancel') }}
          </button>
          <button @click="confirmDelete" class="confirm-delete-btn" :disabled="deleting">
            {{ deleting ? $t('common.deleting') : $t('userManagement.deleteUser') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth.js'
import { usePayrollStore } from '../stores/payroll.js'
import CreateUpdateUserModal from './CreateUpdateUserModal.vue'
import ImportUsersModal from './ImportUsersModal.vue'

const { t } = useI18n()

const authStore = useAuthStore()
const payrollStore = usePayrollStore()

const isSuperuser = computed(() => {
  const superuserStatus = authStore.isSuperuser
  console.log('Checking superuser:', superuserStatus, 'user.is_superuser:', authStore.user?.is_superuser, 'Full user:', authStore.user)
  return superuserStatus
})

const loading = ref(false)
const saving = ref(false)
const deleting = ref(false)
const message = ref('')
const messageType = ref('success')
const users = ref([])
const searchQuery = ref('')
const roleFilter = ref('')
const showCreateForm = ref(false)
const showImportModal = ref(false)
const editingUser = ref(null)
const userToDelete = ref(null)

const currentUserId = computed(() => authStore.user?.id)

const userForm = ref({
  // User basic fields
  first_name: '',
  last_name: '',
  username: '',
  email: '',
  role: '',
  phone: '',
  date_of_birth: '',
  social_security_number: '',
  address: '',
  employment_date: '',
  password: '',
  
  // Payroll fields (stored separately on backend)
  payroll: {
    monthly_salary: null,
    hourly_salary: null,
    employment_rate: null,
    employment_hours_per_week: null,
    employee_tax_table: 30,  // Default to standard tax table
    employment_type: ''
  }
  })

const filteredUsers = computed(() => {
  let filtered = users.value

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(user => 
      user.first_name.toLowerCase().includes(query) ||
      user.last_name.toLowerCase().includes(query) ||
      user.username.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query)
    )
  }

  // Filter by role
  if (roleFilter.value) {
    filtered = filtered.filter(user => user.role === roleFilter.value)
  }

  return filtered
})

// Use payroll store's selected month instead of local selectedDate
const selectedDate = computed({
  get: () => payrollStore.selectedMonth,
  set: (value) => payrollStore.setSelectedMonth(value, users.value)
})

// Computed totals for performance optimization
const totalGrossSalary = computed(() => {
  try {
    return filteredUsers.value.reduce((sum, user) => {
      return sum + payrollStore.getUserGrossSalary(user.id)
    }, 0)
  } catch (error) {
    console.warn('Error calculating total gross salary:', error)
    return 0
  }
})

const totalEmployeeTax = computed(() => {
  try {
    return filteredUsers.value.reduce((sum, user) => {
      return sum + payrollStore.getUserEmployeeTax(user.id)
    }, 0)
  } catch (error) {
    console.warn('Error calculating total employee tax:', error)
    return 0
  }
})

const totalEmployerTax = computed(() => {
  try {
    return filteredUsers.value.reduce((sum, user) => {
      return sum + payrollStore.getUserEmployerTax(user.id)
    }, 0)
  } catch (error) {
    console.warn('Error calculating total employer tax:', error)
    return 0
  }
})

const totalWorkerPayments = computed(() => {
  return filteredUsers.value.reduce((sum, user) => {
    if (hasPayrollData(user)) {
      sum += payrollStore.getUserNetSalary(user.id)
    }
    return sum
  }, 0)
})

// Computed OB hours totals for performance optimization
const totalOB1Hours = computed(() => {
  return filteredUsers.value.reduce((total, user) => {
    const userOB = payrollStore.getUserOBHours(user.id)
    return total + parseFloat(userOB?.ob1_hours || 0)
  }, 0).toFixed(1) + 'h'
})

const totalOB2Hours = computed(() => {
  return filteredUsers.value.reduce((total, user) => {
    const userOB = payrollStore.getUserOBHours(user.id)
    return total + parseFloat(userOB?.ob2_hours || 0)
  }, 0).toFixed(1) + 'h'
})

const totalOB3Hours = computed(() => {
  return filteredUsers.value.reduce((total, user) => {
    const userOB = payrollStore.getUserOBHours(user.id)
    return total + parseFloat(userOB?.ob3_hours || 0)
  }, 0).toFixed(1) + 'h'
})

// Performance optimization: Template helper for repeated conditions
const hasPayrollData = (user) => {
  return user.payroll?.monthly_salary || user.payroll?.hourly_salary
}

onMounted(() => {
  // Check if user is authenticated
  if (!authStore.isAuthenticated) {
    window.location.href = '/'
    return
  }
  loadUsers()
})

async function loadUsers() {
  try {
    loading.value = true
    const response = await authStore.makeAuthenticatedRequest('/api/users/')
    if (response.status === 401 || response.status === 403) {
      showMessage(t('userManagement.sessionExpired'), 'error')
      setTimeout(() => {
        window.location.href = '/'
      }, 1000)
      return
    }
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    const userList = await response.json()
    
    // Optimized parallel API loading with batched requests
    const [fullUsersResults, payrollDataResults] = await Promise.all([
      // Batch 1: Parallel user detail loading (already optimized)
      Promise.all(userList.map(async (u) => {
        try {
          const res = await authStore.makeAuthenticatedRequest(`/api/users/${u.id}/`)
          return res.ok ? await res.json() : u
        } catch (e) {
          return u
        }
      })),
      // Batch 2: Parallel payroll loading for users that might need it
      Promise.all(userList.map(async (u) => {
        try {
          const payrollRes = await authStore.makeAuthenticatedRequest(`/api/payroll/?user=${u.id}`)
          if (payrollRes.ok) {
            const payrollData = await payrollRes.json()
            return {
              userId: u.id,
              payroll: Array.isArray(payrollData) && payrollData.length > 0 
                ? payrollData[0] 
                : (!Array.isArray(payrollData) ? payrollData : null)
            }
          }
        } catch (e) {
          return { userId: u.id, payroll: null }
        }
        return { userId: u.id, payroll: null }
      }))
    ])

    // Create payroll lookup map for O(1) access
    const payrollLookup = new Map()
    payrollDataResults.forEach(result => {
      if (result.payroll) {
        payrollLookup.set(result.userId, result.payroll)
      }
    })

    // Merge results efficiently
    const fullUsers = fullUsersResults.map(user => {
      // Use existing payroll data if present and valid, otherwise use fetched data
      if (!user.payroll || !user.payroll.employment_type) {
        const fetchedPayroll = payrollLookup.get(user.id)
        if (fetchedPayroll) {
          user.payroll = fetchedPayroll
        }
      }
      return user
    })
    
    users.value = fullUsers

    // Load payroll data using the centralized payroll store - no timeout needed
    try {
      await payrollStore.loadMonthlyData(fullUsers)
    } catch (error) {
      console.warn('Could not load monthly payroll data:', error)
    }
  } catch (error) {
    showMessage(t('userManagement.failedToLoadUsers') + ': ' + error.message, 'error')
  } finally {
    loading.value = false
  }
}

// Removed: Using payroll store for data loading

async function loadMonthlyAssignedHours() {
  try {
    const year = selectedDate.value.getFullYear()
    const month = selectedDate.value.getMonth() + 1 // API expects 1-indexed month
    
    // Calculate assigned/actual hours per user
    const userAssignedHours = {}
    
    // For each user, get their hours for the month
    for (const user of users.value) {
      try {
        const employmentType = user?.payroll?.employment_type
        let totalHours = 0
        
        if (employmentType === 'call_in') {
          // Call-in: use ACTUAL time reported hours
          const timeEntries = await timeEntryApi.getTimeEntries({ 
            worker: user.id,
            start_date: `${year}-${String(month).padStart(2, '0')}-01`,
            end_date: `${year}-${String(month).padStart(2, '0')}-31`
          })
          
          if (timeEntries && Array.isArray(timeEntries)) {
            timeEntries.forEach(entry => {
              // Calculate duration from start and end times
              if (entry.start_time && entry.end_time) {
                const startTime = new Date(entry.start_time)
                const endTime = new Date(entry.end_time)
                const durationMs = endTime - startTime
                let durationHours = durationMs / (1000 * 60 * 60)
                
                // Subtract break duration if present
                if (entry.break_duration) {
                  durationHours -= entry.break_duration / 60 // break_duration is in minutes
                }
                
                totalHours += durationHours
              }
            })
          }
        } else {
          // Monthly workers: use assigned shift hours
          const startDate = new Date(year, month - 1, 1)
          const endDate = new Date(year, month, 0, 23, 59, 59)
          const assignments = await shiftApi.getWorkerShiftsInRange(user.id, startDate, endDate)
          
          if (assignments && Array.isArray(assignments)) {
            assignments.forEach(assignment => {
              // Backend includes shift details in the assignment
              const shift = assignment.shift_details || assignment.shift
              
              if (shift && shift.start_time && shift.end_time) {
                const shiftStart = new Date(shift.start_time)
                const shiftEnd = new Date(shift.end_time)
                const durationMs = shiftEnd - shiftStart
                let durationHours = durationMs / (1000 * 60 * 60)
                
                // Apply same break logic as time entries:
                // If shift is 5.5+ hours, subtract 0.5 hours break
                if (durationHours >= 5.5) {
                  durationHours -= 0.5
                }
                
                totalHours += durationHours
              }
            })
          }
        }
        
        if (totalHours > 0) {
          userAssignedHours[user.id] = totalHours
        }
      } catch (error) {
        console.warn(`Could not load hours for user ${user.id}:`, error)
      }
    }
    
    monthlyAssignedHours.value = userAssignedHours
  } catch (error) {
    console.error('Failed to load monthly hours:', error)
    monthlyAssignedHours.value = {}
  }
}

async function loadMonthlySickLeaves() {
  try {
    const year = selectedDate.value.getFullYear()
    const month = selectedDate.value.getMonth() + 1
    
    const sickLeavesMap = {}
    const workDaysMap = {}
    const shiftHoursMap = {}
    
    for (const user of users.value) {
      try {
        // Load sick leaves for this user
        const userSickLeaves = await sickLeaveApi.getSickLeaves({
          worker_id: user.id,
          start_date: `${year}-${String(month).padStart(2, '0')}-01`,
          end_date: `${year}-${String(month).padStart(2, '0')}-31`
        })
        
        // WORKAROUND: Filter sick leaves on frontend since backend isn't filtering properly
        const filteredSickLeaves = userSickLeaves.filter(sl => sl.worker === user.id)
        
        // Store the full sick leave objects (they contain deduction_amount calculated by backend)
        sickLeavesMap[user.id] = filteredSickLeaves
        
        // Extract dates from sick leaves for reference
        const sickLeaveDates = filteredSickLeaves.map(sl => sl.start_date)
        
        // Get work days from shifts (days with assignments)
        const startDate = new Date(year, month - 1, 1)
        const endDate = new Date(year, month, 0) // Last day of month
        
        const shiftsResponse = await shiftApi.getShiftsInRange(startDate, endDate)
        
        // Find days where this user had assignments and calculate shift hours
        const userWorkDays = new Set()
        const userShiftHours = {}
        
        // First pass: collect shifts where user has assignments
        shiftsResponse.forEach(shift => {
          const shiftDate = shift.start_time.split('T')[0]
          
          if (shift.assignments) {
            const userAssignment = shift.assignments.find(a => a.worker === user.id)
            if (userAssignment) {
              userWorkDays.add(shiftDate)
              
              // Calculate shift duration in hours
              const startTime = new Date(shift.start_time)
              const endTime = new Date(shift.end_time)
              const durationMs = endTime - startTime
              let durationHours = durationMs / (1000 * 60 * 60)
              
              // Apply same break logic as time entries:
              // If shift is 5.5+ hours, subtract 0.5 hours break
              if (durationHours >= 5.5) {
                durationHours -= 0.5
              }
              
              // Store shift hours for this date
              userShiftHours[shiftDate] = durationHours
            }
          }
        })
        
        // Second pass: for sick leave dates without shift hours, find any shift on that date
        // This handles cases where user reported sick and assignment was removed
        sickLeaveDates.forEach(sickDate => {
          if (!userShiftHours[sickDate]) {
            // Find any shift on this date - user was likely scheduled for it before reporting sick
            const shiftsOnDate = shiftsResponse.filter(shift => {
              const shiftDate = shift.start_time.split('T')[0]
              return shiftDate === sickDate
            })
            
            if (shiftsOnDate.length > 0) {
              // Use the first shift's hours (or sum if multiple - for now use first)
              const shift = shiftsOnDate[0]
              const startTime = new Date(shift.start_time)
              const endTime = new Date(shift.end_time)
              const durationMs = endTime - startTime
              let durationHours = durationMs / (1000 * 60 * 60)
              
              // Apply same break logic as time entries:
              // If shift is 5.5+ hours, subtract 0.5 hours break
              if (durationHours >= 5.5) {
                durationHours -= 0.5
              }
              
              userShiftHours[sickDate] = durationHours
            } else {
              console.warn(`No shift found for sick date ${sickDate} for user ${user.id}`)
            }
          }
        })
        
        workDaysMap[user.id] = Array.from(userWorkDays)
        shiftHoursMap[user.id] = userShiftHours
      } catch (error) {
        console.warn(`Could not load sick leaves for user ${user.id}:`, error)
        sickLeavesMap[user.id] = []
        workDaysMap[user.id] = []
        shiftHoursMap[user.id] = {}
      }
    }
    
    monthlySickLeaves.value = sickLeavesMap
    monthlyWorkDays.value = workDaysMap
    monthlyShiftHours.value = shiftHoursMap
  } catch (error) {
    console.error('Failed to load monthly sick leaves:', error)
    monthlySickLeaves.value = {}
    monthlyWorkDays.value = {}
    monthlyShiftHours.value = {}
  }
}

function formatOBHours(userId, obType) {
  try {
    const userOB = payrollStore.getUserOBHours(userId)
    if (!userOB) return '-'
    const hours = parseFloat(userOB[obType] || 0)
    if (hours === 0) return '-'
    return hours.toFixed(1) + 'h'
  } catch (error) {
    console.warn('Error formatting OB hours:', error)
    return '-'
  }
}



function calculateBaseSalary(user) {
  return payrollStore.getUserBaseSalary(user.id)
}

function calculateGrossSalary(user) {
  return payrollStore.getUserGrossSalary(user.id)
}

function calculateEmployeeTax(user) {
  return payrollStore.getUserEmployeeTax(user.id)
}

// Removed: Using payroll store for tax calculations

function calculateEmployerTax(user) {
  return payrollStore.getUserEmployerTax(user.id)
}

function formatCurrency(amount) {
  return payrollStore.formatCurrency(amount)
}

// Removed: Using payroll store calculations and computed properties for performance

// Month navigation functions
async function previousMonth() {
  const newDate = new Date(selectedDate.value.getFullYear(), selectedDate.value.getMonth() - 1, 1)
  await payrollStore.setSelectedMonth(newDate, users.value)
}

async function nextMonth() {
  const newDate = new Date(selectedDate.value.getFullYear(), selectedDate.value.getMonth() + 1, 1)
  await payrollStore.setSelectedMonth(newDate, users.value)
}

function formatMonthYear(date) {
  const { locale } = useI18n()
  const localeCode = locale.value === 'sv' ? 'sv-SE' : 'en-US'
  return date.toLocaleDateString(localeCode, { year: 'numeric', month: 'long' })
}

// Removed: Total calculation functions replaced by computed properties for performance optimization

function editUser(user) {
  editingUser.value = user
  userForm.value = {
    // User basic fields
    first_name: user.first_name,
    last_name: user.last_name,
    username: user.username,
    email: user.email,
    role: user.role,
    phone: user.phone || '',
    date_of_birth: user.date_of_birth || '',
    social_security_number: user.social_security_number || '',
    address: user.address || '',
    employment_date: user.employment_date || '',
    password: '',
    // Payroll fields from nested payroll object
    payroll: {
      monthly_salary: user.payroll?.monthly_salary || null,
      hourly_salary: user.payroll?.hourly_salary || null,
      employment_rate: user.payroll?.employment_rate || null,
      employment_hours_per_week: user.payroll?.employment_hours_per_week || null,
      employee_tax_table: user.payroll?.employee_tax_table || 30,
      employment_type: user.payroll?.employment_type || ''
    }
  }
}

async function saveUser() {
  try {
    saving.value = true
    
    // User data (basic info only - no payroll fields)
    // Note: company field is NOT sent - backend automatically assigns manager's company
    const userData = {
      first_name: userForm.value.first_name,
      last_name: userForm.value.last_name,
      username: userForm.value.username,
      email: userForm.value.email,
      role: userForm.value.role,
      phone: userForm.value.phone,
      date_of_birth: userForm.value.date_of_birth,
      social_security_number: userForm.value.social_security_number,
      address: userForm.value.address,
      employment_date: userForm.value.employment_date,
      password: userForm.value.password
    }
    
    if (editingUser.value && !userData.password) {
      delete userData.password // Don't send empty password on edit
    }
    
    // Remove empty or null fields to avoid validation errors
    Object.keys(userData).forEach(key => {
      if (userData[key] === '' || userData[key] === null) {
        delete userData[key]
      }
    })
    
    // Password is required for new users
    if (!editingUser.value && !userData.password) {
      throw new Error(t('userManagement.passwordRequired') || 'Password is required for new users')
    }

    let response
    let savedUser
    
    if (editingUser.value) {
      // Update existing user
      response = await authStore.makeAuthenticatedRequest(`/api/users/${editingUser.value.id}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      })
    } else {
      // Create new user
      response = await authStore.makeAuthenticatedRequest('/api/users/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      })
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error("User creation error response:", errorData)
      throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`)
    }

    savedUser = await response.json()
    
    // Now save payroll data separately
    const payrollData = {
      user: savedUser.id,
      monthly_salary: userForm.value.payroll.monthly_salary,
      hourly_salary: userForm.value.payroll.hourly_salary,
      employment_rate: userForm.value.payroll.employment_rate,
      employment_hours_per_week: userForm.value.payroll.employment_hours_per_week,
      employee_tax_table: userForm.value.payroll.employee_tax_table,
      employment_type: userForm.value.payroll.employment_type
    }
    
    // Check if payroll exists for this user
    let payrollResponse
    let existingPayrollId = editingUser.value?.payroll?.id || savedUser.payroll?.id
    
    // Also check if payroll exists by querying the API
    if (!existingPayrollId && savedUser.id) {
      try {
        const checkPayroll = await authStore.makeAuthenticatedRequest(`/api/payroll/?user=${savedUser.id}`)
        const payrollList = await checkPayroll.json()
        if (Array.isArray(payrollList) && payrollList.length > 0) {
          existingPayrollId = payrollList[0].id
        }
      } catch (e) {
        // No existing payroll found, will create new one
      }
    }
    
    if (existingPayrollId) {
      // Update existing payroll
      payrollResponse = await authStore.makeAuthenticatedRequest(`/api/payroll/${existingPayrollId}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payrollData)
      })
    } else {
      // Create new payroll
      console.log("Creating payroll with data:", payrollData)
      payrollResponse = await authStore.makeAuthenticatedRequest(`/api/payroll/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payrollData)
      })
    }
    
    if (!payrollResponse.ok) {
      const errorData = await payrollResponse.json().catch(() => ({}))
      console.error('Payroll save failed:', errorData)
      throw new Error(`Payroll save failed: ${errorData.detail || errorData.message || `HTTP ${payrollResponse.status}`}`)
    }
    
    const savedPayroll = await payrollResponse.json()
    
    // Merge payroll data into savedUser for consistency
    savedUser.payroll = savedPayroll
    
    if (editingUser.value) {
      // Update existing user in list
      const index = users.value.findIndex(u => u.id === editingUser.value.id)
      if (index !== -1) {
        users.value[index] = savedUser
      }
      showMessage(t('userManagement.userUpdatedSuccess'), 'success')
    } else {
      // Add new user to list
      users.value.push(savedUser)
      showMessage(t('userManagement.userCreatedSuccess'), 'success')
    }
    
    closeModal()
  } catch (error) {
    showMessage(t('userManagement.failedToSaveUser') + ': ' + error.message, 'error')
  } finally {
    saving.value = false
  }
}

async function deleteUser(user) {
  userToDelete.value = user
}

async function confirmDelete() {
  if (!userToDelete.value) return

  try {
    deleting.value = true
    const response = await authStore.makeAuthenticatedRequest(`/api/users/${userToDelete.value.id}/`, {
      method: 'DELETE'
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`)
    }
    
    users.value = users.value.filter(u => u.id !== userToDelete.value.id)
    showMessage(t('userManagement.userDeletedSuccess'), 'success')
    cancelDelete()
  } catch (error) {
    showMessage(t('userManagement.failedToDeleteUser') + ': ' + error.message, 'error')
  } finally {
    deleting.value = false
  }
}

function cancelDelete() {
  userToDelete.value = null
  deleting.value = false
}

function closeModal() {
  showCreateForm.value = false
  editingUser.value = null
  userForm.value = {
    // User basic fields
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    role: '',
    phone: '',
    date_of_birth: '',
    social_security_number: '',
    address: '',
    employment_date: '',
    password: '',
    // Payroll fields
    payroll: {
      monthly_salary: null,
      hourly_salary: null,
      employment_rate: null,
      employment_hours_per_week: null,
      employee_tax_table: 30,
      employment_type: ''
    }
  }
}

function showMessage(msg, type) {
  message.value = msg
  messageType.value = type
  setTimeout(() => {
    message.value = ''
  }, 5000)
}

function handleImportComplete() {
  showImportModal.value = false
  fetchUsers() // Reload users list
  showMessage(t('userManagement.importSuccess'), 'success')
}
</script>

<style scoped>
.user-management {
  max-width: 1200px;
  margin: 1rem auto;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.import-btn {
  padding: 0.75rem 1.5rem;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
}

.import-btn:hover {
  background: #5568d3;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.create-user-btn {
  padding: 0.75rem 1.5rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.create-user-btn:hover {
  background: #218838;
  transform: translateY(-1px);
}

.filters {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 2rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;;
  gap: 0.5rem;
}

.filter-group-left {
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.filter-group label {
  font-weight: 600;
  color: #333;
}

.search-input,
.filter-group select {
  padding: 0.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
}

.search-input:focus,
.filter-group select:focus {
  outline: none;
  border-color: #667eea;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading p {
  color: #64748b;
  font-weight: 500;
  margin: 0;
}

.no-users {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 4rem 2rem;
}

.empty-state {
  text-align: center;
  max-width: 400px;
  margin: 0 auto;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1.5rem;
  opacity: 0.6;
}

.empty-state h3 {
  color: #2d3748;
  margin-bottom: 0.75rem;
  font-size: 1.25rem;
}

.empty-state p {
  color: #64748b;
  line-height: 1.6;
  margin: 0;
}

.users-table {
  background: white;
  border-radius: 12px;
  overflow-x: auto;
  overflow-y: visible;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(226, 232, 240, 0.8);
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  position: relative;
}

/* Scroll indicator shadow on mobile */
@media (max-width: 768px) {
  .users-table::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 30px;
    background: linear-gradient(to left, rgba(0, 0, 0, 0.1), transparent);
    pointer-events: none;
    border-radius: 0 12px 12px 0;
  }
}

.users-table table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  min-width: 900px; /* Minimum width to trigger horizontal scroll on small screens */
}

.users-table thead {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.users-table th {
  padding: 1.25rem 1rem;
  text-align: center;
  font-weight: 700;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
}

.users-table th:last-child {
  border-right: none;
}

.users-table tbody tr {
  border-bottom: 1px solid rgba(226, 232, 240, 0.3);
  transition: all 0.2s ease;
  background: white;
}

.users-table tbody tr:hover {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.02) 0%, rgba(118, 75, 162, 0.02) 100%);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.users-table tbody tr:last-child {
  border-bottom: none;
}

.users-table td {
  padding: 0.75rem 0.5rem;
  font-size: 0.925rem;
  border-right: 1px solid rgba(226, 232, 240, 0.2);
  vertical-align: middle;
}

.users-table .totals-row {
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.1) 0%, rgba(34, 136, 56, 0.1) 100%);
  border-top: 2px solid #28a745;
  font-weight: 700;
}

.users-table .totals-row td {
  padding: 1.5rem 1rem;
  font-size: 1rem;
  color: #1a5928;
}

.users-table .totals-row:hover {
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.15) 0%, rgba(34, 136, 56, 0.15) 100%);
  transform: none;
}

.users-table td:last-child {
  border-right: none;
}

.user-name {
  font-weight: 600;
  color: #2d3748;
}

.salary {
  font-weight: 600;
  color: #38a169;
}

.no-salary {
  color: #a0aec0;
  font-style: italic;
}

.total-ob {
  font-weight: 600;
  color: #ed8936;
}

.payroll {
  font-weight: 700;
  color: #667eea;
}

.no-data {
  color: #a0aec0;
  font-style: italic;
}

.ob-breakdown,
.payroll-breakdown {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
}

.ob-hours-summary {
  background: rgba(102, 126, 234, 0.05);
  border-radius: 6px;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
}

.ob-type {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.1rem 0;
}

.ob-type-label {
  font-size: 0.75rem;
  color: #555;
  font-weight: 500;
}

.ob-hours {
  font-weight: 600;
  color: #667eea;
  font-size: 0.8rem;
}

.ob-status-breakdown {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.ob-approved,
.ob-pending,
.ob-total,
.payroll-base,
.payroll-ob,
.payroll-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
}

.ob-approved {
  background: rgba(72, 187, 120, 0.1);
  border: 1px solid rgba(72, 187, 120, 0.2);
}

.ob-pending {
  background: rgba(237, 137, 54, 0.1);
  border: 1px solid rgba(237, 137, 54, 0.2);
}

.ob-total {
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.2);
  font-weight: 600;
}

.payroll-base {
  background: rgba(56, 161, 105, 0.1);
  border: 1px solid rgba(56, 161, 105, 0.2);
}

.payroll-ob {
  background: rgba(237, 137, 54, 0.1);
  border: 1px solid rgba(237, 137, 54, 0.2);
}

.payroll-total {
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.2);
  font-weight: 700;
}

.ob-label,
.payroll-label {
  font-size: 0.75rem;
  opacity: 0.8;
}

.ob-amount,
.payroll-amount {
  font-weight: 600;
}

.ob-amount.total,
.payroll-amount.total {
  color: #667eea;
  font-weight: 700;
}

.role-badge {
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
}

.role-badge.worker {
  background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(66, 153, 225, 0.3);
}

.role-badge.manager {
  background: linear-gradient(135deg, #ed8936 0%, #dd6b20 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(237, 137, 54, 0.3);
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  justify-content: flex-start;
}

.action-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  min-width: 70px;
  justify-content: center;
}

.action-btn.edit {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(72, 187, 120, 0.3);
}

.action-btn.edit:hover {
  background: linear-gradient(135deg, #38a169 0%, #2f855a 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(72, 187, 120, 0.4);
}

.action-btn.delete {
  background: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(245, 101, 101, 0.3);
}

.action-btn.delete:hover:not(:disabled) {
  background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(245, 101, 101, 0.4);
}

.action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.close-btn {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: 1.2rem;
  cursor: pointer;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.close-btn:hover {
  background: rgba(239, 68, 68, 0.9);
  color: white;
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.user-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #333;
}

.form-input {
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
}

.cancel-btn {
  padding: 0.75rem 1.5rem;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.cancel-btn:hover {
  background: #5a6268;
}

.save-btn {
  padding: 0.75rem 1.5rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.save-btn:hover:not(:disabled) {
  background: #0056b3;
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading,
.no-users {
  text-align: center;
  padding: 3rem;
  color: #666;
  font-size: 1.1rem;
}

.message {
  position: fixed;
  top: 1rem;
  right: 1rem;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  z-index: 1001;
}

.message.success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.message.error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

/* Delete Modal Styles */
.delete-modal {
  max-width: 450px;
  width: 90%;
}

.delete-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.delete-header h3 {
  color: #e53e3e;
  margin: 0;
  font-size: 1.5rem;
}

.delete-body {
  text-align: center;
  margin-bottom: 2rem;
}

.delete-body p {
  color: #4a5568;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.user-info {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
}

.user-info strong {
  display: block;
  color: #2d3748;
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
}

.user-detail {
  color: #718096;
  font-size: 0.9rem;
}

.warning-text {
  color: #e53e3e;
  font-weight: 600;
  font-size: 0.9rem;
}

.delete-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.cancel-delete-btn {
  padding: 0.75rem 1.5rem;
  background: #e2e8f0;
  color: #4a5568;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.cancel-delete-btn:hover {
  background: #cbd5e0;
  transform: translateY(-1px);
}

.confirm-delete-btn {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(245, 101, 101, 0.3);
}

.confirm-delete-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(245, 101, 101, 0.4);
}

.confirm-delete-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Responsive */
@media (max-width: 768px) {
  .user-management {
    margin: 0.5rem;
    padding: 0.75rem;
  }
  
  .header-section {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .filters {
    flex-direction: column;
    gap: 1rem;
  }
  
  .search-input {
    min-width: auto;
  }
  
  /* Make table horizontally scrollable on mobile */
  .users-table {
    border-radius: 8px;
    margin: 0 -0.75rem; /* Extend to edges */
    width: calc(100% + 1.5rem);
  }
  
  .users-table table {
    min-width: 1200px; /* Force horizontal scroll for wider table */
  }
  
  /* Smaller text for mobile */
  .users-table th {
    font-size: 0.7rem;
    padding: 0.75rem 0.4rem;
    white-space: normal; /* Allow header text to wrap */
    line-height: 1.3;
    word-break: break-word;
  }
  
  .users-table td {
    font-size: 0.75rem;
    padding: 0.75rem 0.5rem;
    white-space: nowrap;
  }
  
  /* Specific column widths for mobile */
  .users-table th:nth-child(1), /* Name */
  .users-table td:nth-child(1) {
    min-width: 100px;
    max-width: 120px;
  }
  
  .users-table th:nth-child(2), /* Role */
  .users-table td:nth-child(2) {
    min-width: 70px;
    max-width: 80px;
  }
  
  .users-table th:nth-child(3), /* Monthly Salary */
  .users-table td:nth-child(3) {
    min-width: 80px;
    max-width: 90px;
  }
  
  .users-table th:nth-child(4), /* OB1 */
  .users-table th:nth-child(5), /* OB2 */
  .users-table th:nth-child(6), /* OB3 */
  .users-table td:nth-child(4),
  .users-table td:nth-child(5),
  .users-table td:nth-child(6) {
    min-width: 50px;
    max-width: 60px;
    padding: 0.5rem 0.25rem;
  }
  
  .users-table th:nth-child(7), /* Gross Salary */
  .users-table th:nth-child(8), /* Employee Tax */
  .users-table th:nth-child(9), /* Employer Tax */
  .users-table td:nth-child(7),
  .users-table td:nth-child(8),
  .users-table td:nth-child(9) {
    min-width: 80px;
    max-width: 90px;
  }
  
  .users-table th:nth-child(10), /* Actions */
  .users-table td:nth-child(10) {
    min-width: 80px;
    max-width: 100px;
  }
  
  /* Shrink text in OB columns */
  .ob-hours-display {
    font-size: 0.7rem;
  }
  
  /* Make action buttons smaller */
  .action-btn {
    padding: 0.4rem 0.6rem;
    font-size: 0.75rem;
  }
  
  /* Smaller totals row text */
  .users-table .totals-row td {
    font-size: 0.75rem;
    padding: 1rem 0.4rem;
  }
  
  .table-header,
  .table-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .header-cell,
  .cell {
    padding: 0.5rem;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  .modal-content {
    width: 95%;
    padding: 0.5rem;
  }
  .month-picker-group {
    min-width: 100%;
  }
  .current-month-small {
    font-size: 0.85rem;
  }
  .role-badge.worker,
  .role-badge.manager {
    font-size: 0.7rem;
    padding: 0.3rem 0.8rem;
    min-width: 70px;
  }
}

/* OB Hours columns width styling */
.users-table th:nth-child(4), /* OB1 column */
.users-table th:nth-child(5), /* OB2 column */
.users-table th:nth-child(6), /* OB3 column */
.users-table td:nth-child(4),
.users-table td:nth-child(5),
.users-table td:nth-child(6) {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  text-align: center;
}

.ob-hours-cell {
  padding: 0.75rem 0.25rem;
  text-align: center;
}

.ob-hours-display {
  font-weight: 600;
  font-size: 0.875rem;
  color: #374151;
}

/* Month Picker Compact Styles */
.month-picker-group {
  min-width: 280px;
}

.month-picker-compact {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem;
  background: white;
  border-radius: 4px;
  border: 1px solid #ccc;
}

.month-nav-btn-small {
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  font-weight: bold;
}

.month-nav-btn-small:hover {
  background: #1976d2;
}

.current-month-small {
  font-size: 0.9rem;
  font-weight: 600;
  color: #0a2544;
  flex: 1;
  text-align: center;
  white-space: nowrap;
}
</style>